import { useEffect } from "react";

/* Adres publiczny serwisu — używany do budowania canonical/OG/JSON-LD.
   Celowo stała, nie zmienna środowiskowa: to jest realny, jeden adres
   pod którym serwis jest dostępny dla użytkowników i dla Google, niezależnie
   od tego, gdzie akurat stoi build (dev, staging). */
export const SITE_URL = "https://prawly.pl";
const DEFAULT_IMAGE = `${SITE_URL}/hero-lawyer.jpg`;

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/* `id` musi być unikalny per blok danych strukturalnych, które strona chce
   utrzymywać jednocześnie (np. "seo-page" dla głównego opisu strony,
   "seo-faq" dla FAQPage) — każdy wywołujący nadpisuje tylko swój. */
export function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/*
 * Ustawia title/description/OG/Twitter/canonical dla aktualnej strony.
 * Wołane w każdym komponencie strony publicznej z realną, unikalną treścią —
 * bez tego wszystkie podstrony pokazywały w Google ten sam tytuł i opis
 * wzięty raz, statycznie, z public/index.html.
 *
 * `title` to PEŁNY tytuł strony (dopisz markę sam, np. "Cennik — prawly.") —
 * hook celowo niczego nie dokleja, żeby uniknąć podwójnej marki tam, gdzie
 * tytuł (np. na stronie głównej) już ją zawiera.
 * `path` to ścieżka względna (np. "/cennik") — potrzebna do canonical i og:url.
 * `jsonLd`, jeśli podane, trafia jako osobny blok danych strukturalnych tej
 * konkretnej strony (np. Article na wpisie bazy wiedzy).
 */
export function useSeo({ title, description, path, image, type = "website", jsonLd }) {
  useEffect(() => {
    const url = path ? `${SITE_URL}${path}` : undefined;
    const img = image || DEFAULT_IMAGE;

    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", "prawly");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);
    upsertLink("canonical", url);
    upsertJsonLd("seo-page", jsonLd);

    return () => upsertJsonLd("seo-page", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, type, jsonLd]);
}
